import React from 'react';
import { AlertTriangle, CheckCircle, Camera, User, FileText, AtSign, CreditCard } from 'lucide-react';
import { Button } from './ui/button';
import { 
  isProfileComplete, 
  getMissingFieldDescriptions, 
  getCompletionPercentage
} from '../utils/profileCompletion';

/**
 * Profile Completion Banner
 * 
 * Non-dismissable banner shown at the top of the reviewer dashboard
 * when a reviewer's profile is incomplete.
 * 
 * Incomplete profiles are hidden from public discovery until all
 * required fields are filled in.
 */
const ProfileCompletionBanner = ({ user, onCompleteClick }) => {
  // Don't show if profile is complete
  if (isProfileComplete(user)) {
    return null;
  }

  const missingFields = getMissingFieldDescriptions(user);
  const completionPercent = getCompletionPercentage(user);

  // Get icon for each field type
  const getFieldIcon = (field) => {
    switch (field) {
      case 'avatar':
        return <Camera className="h-4 w-4" />;
      case 'displayName':
        return <User className="h-4 w-4" />;
      case 'username':
        return <AtSign className="h-4 w-4" />;
      case 'bio':
        return <FileText className="h-4 w-4" />;
      case 'stripe':
        return <CreditCard className="h-4 w-4" />;
      default:
        return <AlertTriangle className="h-4 w-4" />;
    }
  };

  return (
    <div 
      className="bg-gradient-to-r from-amber-50 to-yellow-50 border-l-4 border-[#F2D857] p-4 shadow-sm"
      data-testid="profile-completion-banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Left side - Warning and Progress */}
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-amber-800">
                Complete your profile to appear in reviewer discovery
              </h3>
              <p className="text-sm text-amber-700 mt-1">
                Your profile is {completionPercent}% complete. Artists can&apos;t find you until you finish setting up.
              </p>
              
              {/* Missing fields list */}
              <div className="mt-3 flex flex-wrap gap-2">
                {missingFields.map((item, index) => (
                  <span 
                    key={index}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/60 rounded-full text-xs text-amber-800 border border-amber-200"
                  >
                    {getFieldIcon(item.field)}
                    {item.message}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Right side - CTA Button */}
          <div className="flex-shrink-0 md:self-center">
            <Button
              onClick={onCompleteClick}
              className="bg-[#F2D857] hover:bg-[#e5cb4a] text-black font-semibold px-6 py-2 shadow-md hover:shadow-lg transition-all"
              data-testid="complete-profile-btn"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Complete Profile
            </Button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-4 max-w-md">
          <div className="flex items-center justify-between text-xs text-amber-700 mb-1">
            <span>Profile completion</span>
            <span>{completionPercent}%</span>
          </div>
          <div className="h-2 bg-amber-100 rounded-full overflow-hidden">
            <div 
              className={`h-full rounded-full transition-all duration-500 ${
                completionPercent === 100 
                  ? 'bg-green-500' 
                  : completionPercent >= 80 
                    ? 'bg-yellow-400' 
                    : completionPercent >= 60 
                      ? 'bg-orange-500' 
                      : 'bg-red-500'
              }`}
              style={{ width: `${completionPercent}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileCompletionBanner;
